import cloneDeep from 'lodash/cloneDeep'
import { unescapeHtml } from 'utils/htmlSymbolHandling'

export const ENGINEERING_INPUT_ITEMS = {
  engineeringCase: {
    label: 'Engineering Case',
    tags: ['HYDRAULICS', 'TORQUE_AND_DRAG', 'SURGE_AND_SWAB', 'CENT_STANDOFF', 'BHA_ANALYSIS', 'WELL_CONTROL'],
  },

  description: {
    label: 'Description',
    tags: ['HYDRAULICS', 'TORQUE_AND_DRAG', 'SURGE_AND_SWAB', 'CENT_STANDOFF', 'BHA_ANALYSIS', 'WELL_CONTROL'],
  },

  trajectory: {
    label: 'Trajectory',
    tags: ['HYDRAULICS', 'TORQUE_AND_DRAG', 'SURGE_AND_SWAB', 'CENT_STANDOFF', 'BHA_ANALYSIS', 'WELL_CONTROL'],
  },

  wellPlan: {
    label: 'Well Plan',
    tags: ['HYDRAULICS', 'TORQUE_AND_DRAG', 'SURGE_AND_SWAB', 'CENT_STANDOFF', 'BHA_ANALYSIS', 'WELL_CONTROL'],
    isVisible: (selectedCase) => selectedCase?.surveyOptions !== 'surveys',
  },

  bha: {
    label: 'BHA',
    tags: ['HYDRAULICS', 'TORQUE_AND_DRAG', 'SURGE_AND_SWAB', 'CENT_STANDOFF', 'BHA_ANALYSIS', 'WELL_CONTROL'],
  },

  holeSection: {
    label: 'Hole Section',
    tags: ['HYDRAULICS', 'TORQUE_AND_DRAG', 'SURGE_AND_SWAB', 'CENT_STANDOFF', 'BHA_ANALYSIS', 'WELL_CONTROL'],
  },
}

const getSelectedTrajectory = (selectedCase) => {
  if (!selectedCase) return []
  let svys = selectedCase?.surveys // composite
  if (selectedCase.surveyOptions === 'plans') svys = selectedCase?.wellPlan
  if (selectedCase.surveyOptions === 'surveys') svys = selectedCase?.actualWellbore

  if (!Array.isArray(svys) || svys.length === 0) return []
  return svys
}

const EngineeringCaseParameterDefinitions = [
  {
    section: 'CalculationSettings',
    objTag: '',
    tag: 'bitDepth',
    label: 'Calc Depth',
    type: 'numeric',
    units: 'Depth',
    precision: 2,
    infoTipTitle: 'Bit / Calculation depth',
    infoTipContent: 'The depth calculation will be performed to',
    tags: ['HYDRAULICS', 'TORQUE_AND_DRAG', 'SURGE_AND_SWAB', 'CENT_STANDOFF', 'BHA_ANALYSIS', 'WELL_CONTROL'],
    minValue: 1,
    maxValue: 100000,
    isVisible: (selectedCase) => selectedCase?.useDepthRange !== true,
    getActionButtons: (selectedCase) => {
      return [
        {
          tooltip: 'Set Calc Depth to the deepest depth of the selected Trajectory',
          icon: 'iconoir:depth',
          getValue: (selectedCase) => {
            const selectedTrajectory = getSelectedTrajectory(selectedCase)
            return selectedTrajectory.length > 0 ? selectedTrajectory[selectedTrajectory.length - 1].md : null
          },
        },
      ]
    },
    actionButtons: [],
  },
  {
    section: 'CalculationSettings',
    objTag: 'hydraulicParams',
    tag: 'standLength',
    label: 'Step Length',
    type: 'numeric',
    units: 'Depth',
    precision: 2,
    infoTipTitle: 'Step / Interval Length',
    infoTipContent: 'The calculation interval, usually one stand is sufficient',
    tags: ['HYDRAULICS', 'TORQUE_AND_DRAG', 'SURGE_AND_SWAB'],
    defaultValue: 100,
    minValue: 10,
    maxValue: 1000,
  },
  {
    section: 'CalculationSettings',
    objTag: 'tndParams',
    tag: 'blockWtRot',
    label: 'Block Weight',
    type: 'numeric',
    units: 'Weight',
    precision: 2,
    infoTipTitle: (selectedCase) => (Boolean(selectedCase?.rigName?.trim()) ? 'Block Weight' : ''),
    infoTipContent: (selectedCase) =>
      Boolean(selectedCase?.rigName?.trim())
        ? 'When the well has a rig selected in the Wells Dashboard, the Block Weight will populate automatically.'
        : '',
    tags: ['TORQUE_AND_DRAG'],
    confirmChange: ({ selectedCase }) => Boolean(selectedCase?.rigName?.trim()),
    confirmTitle: 'Block Weight',
    confirmText: 'This will change the block weight from the default value. Do you want to continue?',
  },
  {
    section: 'CalculationSettings',
    objTag: '',
    tag: 'rigName',
    label: 'Rig Name',
    type: 'text',
    units: '',
    precision: 0,
    infoTipTitle: 'Rig Name',
    infoTipContent:
      'This Cell is automatically populated based on the rig which has been selected in the Wells Dashboard',
    defaultValue: '',
    isVisible: (selectedCase) => Boolean(selectedCase?.rigName?.trim()),
    readOnly: true,
  },
  {
    section: 'CalculationSettings',
    objTag: '',
    tag: 'useDepthRange',
    label: 'Depth Range',
    type: 'boolean',
    tags: ['HYDRAULICS', 'TORQUE_AND_DRAG', 'SURGE_AND_SWAB', 'CENT_STANDOFF', 'WELL_CONTROL'],
    infoTipTitle: 'Use Depth Range',
    infoTipContent:
      'If enabled, then start depth/end depth parameters will represent the min/max Y Axis values on the chart',
    validate: (value, selectedCase) => {
      if (!value) return true
      const startDepth = selectedCase?.startDepth
      const endDepth = selectedCase?.bitDepth
      if (typeof startDepth !== 'number' || typeof endDepth !== 'number') return true

      if (startDepth < 0 || endDepth < 0) return false

      return endDepth > startDepth
    },
    validityGroup: 'depthRange',
  },
  {
    section: 'CalculationSettings',
    objTag: '',
    tag: 'startDepth',
    label: 'Start Depth',
    type: 'numeric',
    units: 'Depth',
    precision: 2,
    defaultValue: 1000,
    minValue: 0,
    maxValue: 49999,
    validate: (value, selectedCase) => {
      if (typeof value !== 'number' || typeof selectedCase?.bitDepth !== 'number') return true
      if (value < 0) return false
      return value < selectedCase.bitDepth
    },
    validityGroup: 'depthRange',
    isVisible: (selectedCase) => selectedCase?.useDepthRange === true,
  },
  {
    section: 'CalculationSettings',
    objTag: '',
    tag: 'bitDepth',
    label: 'End Depth', // this is the same as bit depth. just aliased to make it more clear when depth range is enabled
    type: 'numeric',
    units: 'Depth',
    precision: 2,
    minValue: 1,
    maxValue: 50000,
    validate: (value, selectedCase) => {
      if (typeof value !== 'number' || typeof selectedCase?.startDepth !== 'number') return true
      if (value < 0) return false
      return value > selectedCase.startDepth
    },
    validityGroup: 'depthRange',
    isVisible: (selectedCase) => selectedCase?.useDepthRange === true,
  },
  {
    section: 'DrillingParameters',
    objTag: 'tndParams',
    tag: 'wobRot',
    label: 'WOB Rotate',
    type: 'numeric',
    units: 'Weight',
    precision: 2,
    tags: ['TORQUE_AND_DRAG'],
  },
  {
    section: 'DrillingParameters',
    objTag: 'tndParams',
    tag: 'wobSlide',
    label: 'WOB Slide',
    type: 'numeric',
    units: 'Weight',
    precision: 2,
    tags: ['TORQUE_AND_DRAG'],
  },
  {
    section: 'DrillingParameters',
    objTag: '',
    tag: 'rpm',
    label: 'RPM',
    type: 'numeric',
    units: '',
    precision: 2,
    infoTipTitle: 'RPM',
    infoTipContent: 'Must be > 0 for drillers torque charts to generate, also used for reaming hookloads and torques',
    minValue: 0,
    maxValue: 1000,
    tags: ['TORQUE_AND_DRAG', 'HYDRAULICS'],
  },
  {
    section: 'DrillingParameters',
    objTag: '',
    tag: 'rop',
    label: 'ROP',
    type: 'numeric',
    units: 'Depth',
    secondaryUnit: '/hr',
    precision: 2,
    infoTipTitle: 'ROP',
    infoTipContent: 'This is used for hole cleaning calculations such as CCI, and minimum flow rate.',
    tags: ['HYDRAULICS'],
  },
  {
    section: 'DrillingParameters',
    objTag: 'tndParams',
    tag: 'flowRate',
    label: 'Flow Rate',
    type: 'numeric',
    units: 'FlowRate',
    precision: 2,
    infoTipTitle: 'Flow Rate',
    infoTipContent:
      'Used for hydraulics and T&D, multiple flow rates for a sensitivity analysis can be entered on the hydraulics tab. \nThis flow rate will always be used for T&D',
    tags: ['HYDRAULICS', 'TORQUE_AND_DRAG'],
  },
  {
    section: 'DrillingParameters',
    objTag: 'tndParams',
    tag: 'tripSpeed',
    label: 'Tripping Speed',
    type: 'numeric',
    units: 'Depth',
    secondaryUnit: '/min',
    precision: 2,
    infoTipTitle: 'Tripping Speed',
    infoTipContent:
      'Used for calculating surge & swab, if a sensitivity analysis is required then multiple tripping speeds can be entered on the surge and swab parameters tab',
    tags: ['SURGE_AND_SWAB'],
  },
  {
    section: 'DrillingParameters',
    objTag: 'tndParams',
    tag: 'reamingSpd',
    label: 'Reaming Speed',
    type: 'numeric',
    units: 'Depth',
    secondaryUnit: '/min',
    precision: 2,
    infoTipTitle: 'Reaming Speed',
    infoTipContent: 'Used for the axial component when calculating reaming hookloads and reaming torques',
    tags: ['TORQUE_AND_DRAG'],
  },
  {
    section: 'Mud',
    objTag: 'fluids',
    tag: 'fluidDesc',
    label: 'Name',
    type: 'text',
    units: '',
    defaultValue: '',
    tags: [],
  },
  {
    section: 'Mud',
    objTag: 'fluids',
    tag: 'mudWeight',
    label: 'Weight',
    type: 'numeric',
    units: 'MudWeight',
    precision: 2,
    tags: ['TORQUE_AND_DRAG', 'HYDRAULICS', 'SURGE_AND_SWAB', 'WELL_CONTROL', 'CENT_STANDOFF', 'BHA_ANALYSIS'],
  },
  {
    section: 'Mud',
    objTag: 'fluids',
    tag: 'pv',
    label: 'PV',
    type: 'numeric',
    units: '',
    precision: 2,
    infoTipTitle: 'Plastic Viscosity (PV)',
    infoTipContent: 'Required for Bingham Plastic model, is equal to the 600 - 300',
    tags: ['TORQUE_AND_DRAG', 'HYDRAULICS', 'SURGE_AND_SWAB', 'WELL_CONTROL', 'CENT_STANDOFF', 'BHA_ANALYSIS'],
  },
  {
    section: 'Mud',
    objTag: 'fluids',
    tag: 'yp',
    label: 'YP',
    type: 'numeric',
    units: '',
    precision: 2,
    infoTipTitle: 'Yield Point (YP)',
    infoTipContent: 'Required for Bingham Plastic model, is equal to the 300 - PV',
    tags: ['TORQUE_AND_DRAG', 'HYDRAULICS', 'SURGE_AND_SWAB', 'WELL_CONTROL', 'CENT_STANDOFF', 'BHA_ANALYSIS'],
  },
  ...['sixhun', 'threehun', 'twohun', 'onehun', 'dialSix', 'dialThree'].map((tag) => ({
    section: 'Mud',
    objTag: 'fluids',
    tag,
    label:
      tag === 'sixhun'
        ? '600'
        : tag === 'threehun'
          ? '300'
          : tag === 'twohun'
            ? '200'
            : tag === 'onehun'
              ? '100'
              : tag === 'dialSix'
                ? '6'
                : '3',
    type: 'numeric',
    units: '',
    precision: 2,

    isVisible: (selectedCase) => selectedCase?.hydraulicParams?.hydModel !== 'Bingham Plastic',
    tags: ['TORQUE_AND_DRAG', 'HYDRAULICS', 'SURGE_AND_SWAB', 'WELL_CONTROL', 'CENT_STANDOFF', 'BHA_ANALYSIS'],
  })),
  {
    section: 'Mud',
    objTag: '',
    tag: 'autoNk',
    label: 'Auto NK',
    type: 'boolean',
    units: '',
    infoTipTitle: 'Auto NK',
    infoTipContent:
      'Allows user to manually specify hydraulic N & K parameters. Advanced use only: recommend keeping on auto',
    tags: ['TORQUE_AND_DRAG', 'HYDRAULICS', 'SURGE_AND_SWAB', 'WELL_CONTROL', 'CENT_STANDOFF'],
  },
  {
    section: 'Mud',
    objTag: 'fluids',
    tag: 'n',
    label: 'N',
    type: 'numeric',
    units: '',
    precision: 4,
    isVisible: (selectedCase) => selectedCase?.autoNk === false,
    tags: ['TORQUE_AND_DRAG', 'HYDRAULICS', 'SURGE_AND_SWAB', 'WELL_CONTROL', 'CENT_STANDOFF'],
  },
  {
    section: 'Mud',
    objTag: 'fluids',
    tag: 'k',
    label: 'K',
    type: 'numeric',
    units: '',
    precision: 4,
    isVisible: (selectedCase) => selectedCase?.autoNk === false,
    tags: ['TORQUE_AND_DRAG', 'HYDRAULICS', 'SURGE_AND_SWAB', 'WELL_CONTROL', 'CENT_STANDOFF'],
  },
  {
    section: 'Hydraulics',
    objTag: 'hydraulicParams',
    tag: 'hydModel',
    label: 'Model',
    type: 'dropDown',
    units: '',
    dropDownField: 'hydraulicModels',
    defaultValue: '',
    tags: ['HYDRAULICS'],
  },
  {
    section: 'Hydraulics',
    objTag: 'hydraulicParams',
    tag: 'surfacePloss',
    label: 'Surface P Loss',
    type: 'numeric',
    units: 'Pressure',
    precision: 2,
    defaultValue: 0,
    minValue: 0,
    maxValue: 1000000,
    infoTipTitle: 'Surface Equipment Pressure Loss',
    infoTipContent: 'Pressure loss for surface equipment such as stand pipe, goose neck etc, 50-200psi is normal',
    tags: ['HYDRAULICS'],
  },
  {
    section: 'Hydraulics',
    objTag: 'hydraulicParams',
    tag: 'ecdAdj',
    label: 'ECD Adjust',
    type: 'numeric',
    units: 'MudWeight',
    precision: 3,
    defaultValue: 0,
    minValue: -100,
    maxValue: 100,
    infoTipTitle: 'ECD Adjustment',
    infoTipContent:
      'Used to manually shift the calculated ECD by a fixed value, used to calibrate model with downhole PWD tools, advanced use only',
    tags: ['TORQUE_AND_DRAG', 'HYDRAULICS', 'SURGE_AND_SWAB'],
  },
  {
    section: 'Hydraulics',
    objTag: 'hydraulicParams',
    tag: 'sppSafety',
    label: 'SPP Safety',
    type: 'numeric',
    units: '',
    secondaryUnit: '%',
    precision: 0,
    defaultValue: 0,
    minValue: 0,
    maxValue: 50,
    infoTipTitle: 'Pump Pressure Safety Margin',
    infoTipContent: 'Increases the calculated pump pressure by the entered percentage',
    tags: ['HYDRAULICS'],
  },
  {
    section: 'Hydraulics',
    objTag: '',
    tag: 'incStabCent',
    label: 'Inc. Stabs',
    type: 'boolean',
    units: '',
    defaultValue: false,
    infoTipTitle: 'Include Stabilizers',
    infoTipContent:
      'Specifies if stabilizer dimensions are taken in to account in ECD and pump pressure calculations, usually only has a small impact on overall results',
    tags: ['HYDRAULICS'],
  },
  {
    section: 'TorqueAndDrag',
    objTag: 'tndParams',
    tag: 'autoBitTq',
    label: 'Auto Bit TQ',
    type: 'boolean',
    units: '',
    infoTipTitle: 'Auto Calculate Bit Torque',
    infoTipContent:
      'Specifies if torque generated by the bit is automatically calculated based on the bit OD and WOB, only used for on bottom torque results',
    tags: ['TORQUE_AND_DRAG'],
  },
  {
    section: 'TorqueAndDrag',
    objTag: 'tndParams',
    tag: 'bitTq',
    label: 'Bit TQ',
    type: 'numeric',
    units: 'Torque',
    precision: 2,
    infoTipTitle: 'Bit Torque',
    infoTipContent: 'Specifies the torque generated by the bit',
    tags: ['TORQUE_AND_DRAG'],
    readOnly: (selectedCase) => selectedCase?.tndParams?.autoBitTq === true,
  },
  {
    section: 'TorqueAndDrag',
    objTag: 'tndParams',
    tag: 'isViscDrag',
    label: 'Viscous Drag',
    type: 'boolean',
    units: '',
    infoTipTitle: 'Include Viscous Drag',
    infoTipContent:
      'Specifies if viscous fluid drag is taken in to account for hookload calculations, ususally not required',
    tags: ['TORQUE_AND_DRAG'],
  },
  {
    section: 'TorqueAndDrag',
    objTag: 'tndParams',
    tag: 'csgWearFac',
    label: 'Casing Wear Factor',
    type: 'numeric',
    units: '',
    precision: 3,
    minValue: 0.1,
    maxValue: 1500,
    infoTipTitle: 'Casing wear factor',
    infoTipContent:
      'Used in the casing wear calculation, a higher value indicates a more abrasive material, a value of 0.3 - 0.5 is normal for WBM / OBM and steel tool joints, a value of 1500 would be used if rough carbide wear bands are used or high sand content mud',
    tags: ['TORQUE_AND_DRAG'],
  },
  {
    section: 'TorqueAndDrag',
    objTag: 'tndParams',
    tag: 'sfUnits',
    label: 'SF Unit Length',
    type: 'numeric',
    units: 'Depth',
    precision: 2,
    minValue: 0,
    maxValue: 1000,
    infoTipTitle: 'SF Unit Units',
    infoTipContent:
      'Defines the length over which side force is calculated (e.g. lbf/ft or kN/m). For casing wear predictions, this should typically be set per tool joint (e.g. 30 ft or 10 m)',
    tags: ['TORQUE_AND_DRAG'],
  },
  {
    section: 'TorqueAndDrag',
    objTag: 'tndParams',
    tag: 'overpull',
    label: 'Overpull',
    type: 'numeric',
    units: 'Weight',
    precision: 2,
    minValue: 0,
    maxValue: 10000000,
    infoTipTitle: 'Overpull',
    infoTipContent: 'Only required for apparent overpull calculation',
    tags: ['TORQUE_AND_DRAG'],
  },
  {
    section: 'SurgeAndSwabParameters',
    objTag: 'hydraulicParams',
    tag: 'surgeAndSwabType',
    label: 'Calc Type',
    type: 'dropDown',
    units: '',
    dropDownField: 'surgeAndSwab',
    tags: ['SURGE_AND_SWAB'],
  },
  {
    section: 'SurgeAndSwabParameters',
    objTag: 'hydraulicParams',
    tag: 'ssRef',
    label: 'Reference',
    type: 'dropDown',
    units: '',
    dropDownField: 'surgeAndSwabRef',
    tags: ['SURGE_AND_SWAB'],
  },
  {
    section: 'SurgeAndSwabParameters',
    objTag: 'hydraulicParams',
    tag: 'ssRefDepth',
    label: 'Ref Depth',
    type: 'numeric',
    precision: 2,
    minValue: 0,
    maxValue: 100000,
    units: 'Depth',
    infoTipTitle: 'User Defined Reference Depth',
    infoTipContent:
      'When user defined reference is selected, this depth will be used as the reference depth for surge and swab calculations',
    isVisible: (selectedCase) => selectedCase?.hydraulicParams?.ssRef === 'User Defined',
    validate: (value, selectedCase) => {
      if (typeof value !== 'number' || typeof selectedCase?.bitDepth !== 'number') return true
      if (value < 0) return false
      return value <= selectedCase.bitDepth
    },
    tags: ['SURGE_AND_SWAB'],
  },
  {
    section: 'SurgeAndSwabParameters',
    objTag: '',
    tag: 'ssMinEmw',
    label: 'Min EMW',
    type: 'numeric',
    units: 'MudWeight',
    precision: 2,
    minValue: 0,
    maxValue: 1000000,
    infoTipTitle: 'Min EMW',
    infoTipContent:
      'Represents the minimum equivalent mud weight seen at the bit during tripping, used for max tripping speed calculations, this is usually pore pressure plus a safety factor',
    tags: ['SURGE_AND_SWAB'],
  },
  {
    section: 'SurgeAndSwabParameters',
    objTag: '',
    tag: 'ssMaxEmw',
    label: 'Max EMW',
    type: 'numeric',
    units: 'MudWeight',
    precision: 2,
    minValue: 0,
    maxValue: 1000000,
    infoTipTitle: 'Max EMW',
    infoTipContent:
      'Represents the maximum equivalent mud weight seen at the bit during tripping, used for max tripping speed calculations, this is usually fracture gradient minus a safety factor',
    tags: ['SURGE_AND_SWAB'],
  },
  {
    section: 'SurgeAndSwabParameters',
    objTag: 'hydraulicParams',
    tag: 'contCirc',
    label: 'Continuous Circ',
    type: 'boolean',
    units: '',
    infoTipTitle: 'Continuous Circulation',
    infoTipContent:
      'Specifies if continuous circulation is used during tripping, this can be used to represent coiled tubing and is used to minimize surge and swab pressures',
    tags: [],
  },
  {
    section: 'SurgeAndSwabParameters',
    objTag: 'hydraulicParams',
    tag: 'contTrip',
    label: 'Continuous Trip',
    type: 'boolean',
    units: '',
    infoTipTitle: 'Continuous Tripping',
    infoTipContent: 'Used to model coiled tubing, if enabled the model ignores acceleration effects and gels',
    tags: ['SURGE_AND_SWAB'],
  },
  {
    section: 'SurgeAndSwabParameters',
    objTag: 'hydraulicParams',
    tag: 'incGelStrength',
    label: 'Inc. Gels',
    type: 'boolean',
    units: '',
    infoTipTitle: 'Include Gel Strength',
    infoTipContent: 'If enabled then the pressure required to break down the gels after a connection will be modelled',
    tags: ['SURGE_AND_SWAB'],
  },
  {
    section: 'SurgeAndSwabParameters',
    objTag: 'hydraulicParams',
    tag: 'incPipeAccel',
    label: 'Pipe Accel.',
    type: 'boolean',
    units: '',
    infoTipTitle: 'Include acceleration effects',
    infoTipContent:
      'If enabled then the additional pressure generated to accelerate the pipe after connections will be included',
    tags: ['SURGE_AND_SWAB'],
  },
  {
    section: 'SurgeAndSwabParameters',
    objTag: 'hydraulicParams',
    tag: 'useBitTfa',
    label: 'Use Bit TFA',
    type: 'boolean',
    units: '',
    infoTipTitle: 'Used bit or show TFA',
    infoTipContent:
      'If open ended calculation is selected and this parameter is enabled the TFA of the bit or casing show will be used for the flow area at the bottom of the drillstring, if disabled and open ended is selected then the ID of the component will be used',
    tags: ['SURGE_AND_SWAB'],
  },
  {
    section: 'SurgeAndSwabParameters',
    objTag: 'hydraulicParams',
    tag: 'limitAccel',
    label: 'Limit Accel.',
    type: 'boolean',
    units: '',
    isVisible: (selectedCase) => selectedCase?.hydraulicParams?.incPipeAccel === true,
    tags: ['SURGE_AND_SWAB'],
  },
  {
    section: 'CasingStandoff',
    objTag: '',
    tag: 'standOffReq',
    label: 'Standoff Required',
    type: 'numeric',
    units: '',
    secondaryUnit: '%',
    precision: 2,
    minValue: 0,
    maxValue: 100,
    infoTipTitle: 'Desired Standoff',
    infoTipContnet: 'Represents a desired standoff on charts, not used in any calculations',
    tags: [],
  },
  {
    section: 'CasingStandoff',
    objTag: '',
    tag: 'cementWt',
    label: 'Lead Cement Wt',
    type: 'numeric',
    units: 'MudWeight',
    precision: 3,
    minValue: 0,
    maxValue: 1000,
    tags: ['CENT_STANDOFF'],
  },
  {
    section: 'CasingStandoff',
    objTag: '',
    tag: 'tailCementWt',
    label: 'Tail Cement Wt',
    type: 'numeric',
    units: 'MudWeight',
    precision: 3,
    minValue: 0,
    maxValue: 1000,
    tags: ['CENT_STANDOFF'],
  },
  {
    section: 'CasingStandoff',
    objTag: '',
    tag: 'floatationFluidWt',
    label: 'Flotation Fluid Wt',
    type: 'numeric',
    units: 'MudWeight',
    precision: 3,
    minValue: 0,
    maxValue: 1000,
    tags: ['CENT_STANDOFF'],
  },
  {
    section: 'WellControl',
    objTag: 'wellControl',
    tag: 'fitLot',
    label: 'LOT / FIT',
    type: 'numeric',
    units: 'MudWeight',
    precision: 2,
    minValue: 0,
    maxValue: 1000000,
    tags: ['WELL_CONTROL'],
  },
  {
    section: 'WellControl',
    objTag: 'wellControl',
    tag: 'gasGrad',
    label: 'Gas Gradient',
    type: 'numeric',
    units: 'Pressure',
    secondaryUnit: '/Depth',
    precision: 3,
    minValue: 0,
    maxValue: 1000000,
    tags: ['WELL_CONTROL'],
  },
  {
    section: 'WellControl',
    objTag: 'wellControl',
    tag: 'kickIntensity',
    label: 'Kick Intensity',
    type: 'numeric',
    units: 'MudWeight',
    precision: 3,
    minValue: 0,
    maxValue: 1000000,
    tags: ['WELL_CONTROL'],
  },
  {
    section: 'WellControl',
    objTag: 'wellControl',
    tag: 'safetyMargin',
    label: 'Safety Margin',
    type: 'numeric',
    units: 'Pressure',
    precision: 2,
    minValue: 0,
    maxValue: 1000000,
    tags: ['WELL_CONTROL'],
  },
  {
    section: 'WellControl',
    objTag: 'wellControl',
    tag: 'pitGain',
    label: 'Pit Gain',
    type: 'numeric',
    units: 'Volume',
    precision: 2,
    minValue: 0,
    maxValue: 1000000,
    tags: ['WELL_CONTROL'],
  },
  {
    section: 'Cementing',
    objTag: 'cementing',
    tag: 'stringDepth',
    label: 'String Depth',
    type: 'numeric',
    units: 'Depth',
    precision: 2,
    minValue: 0,
    maxValue: 1000000,
    tags: ['CEMENTING'],
  },
  {
    section: 'Cementing',
    objTag: 'cementing',
    tag: 'floatDepth',
    label: 'Float Depth',
    type: 'numeric',
    units: 'Depth',
    precision: 2,
    minValue: 0,
    maxValue: 1000000,
    tags: ['CEMENTING'],
  },
  {
    section: 'Cementing',
    objTag: 'cementing',
    tag: 'pumpOutput',
    label: 'Pump Output',
    type: 'numeric',
    units: 'FlowRate',
    precision: 4,
    minValue: 0,
    maxValue: 1000,
    tags: ['CEMENTING'],
  },
  {
    section: 'Cementing',
    objTag: 'cementing',
    tag: 'singleCalc',
    label: 'Single Calc',
    type: 'boolean',
    units: '',
    tags: ['CEMENTING'],
  },
  {
    section: 'Cementing',
    objTag: 'cementing',
    tag: 'singleCalcStrokes',
    label: 'Single Calc Strokes',
    type: 'numeric',
    units: '',
    precision: 0,
    minValue: 0,
    maxValue: 1000,
    tags: ['CEMENTING'],
  },
  {
    section: 'Cementing',
    objTag: 'cementing',
    tag: 'singleCalcStrokesmpdBackPressure',
    label: 'MPD Back Pressure',
    type: 'numeric',
    units: 'Pressure',
    precision: 2,
    minValue: 0,
    maxValue: 10000,
    tags: ['CEMENTING'],
  },
  {
    section: 'Cementing',
    objTag: 'cementing',
    tag: 'innerStringPresent',
    label: 'Use Inner String',
    type: 'boolean',
    units: '',
    tags: ['CEMENTING'],
  },
]

function readValueFromSelectedCase(selectedCase, definition) {
  if (!selectedCase || !definition) return undefined

  const objectName = typeof definition.objTag === 'string' ? definition.objTag : ''
  const propertyName = typeof definition.tag === 'string' ? definition.tag : ''

  if (!propertyName) return undefined

  if (objectName) {
    const nestedObject = selectedCase?.[objectName]
    return nestedObject?.[propertyName]
  }

  return selectedCase?.[propertyName]
}

function buildDefinitionsForSection(selectedCase, sectionName) {
  const allDefinitions = cloneDeep(EngineeringCaseParameterDefinitions)

  const builtDefinitions = []
  for (let definitionIndex = 0; definitionIndex < allDefinitions.length; definitionIndex++) {
    const definition = allDefinitions[definitionIndex]
    if (!definition || definition.section !== sectionName) continue

    const currentValue = readValueFromSelectedCase(selectedCase, definition)

    const valueWithFallback =
      currentValue === undefined || currentValue === null
        ? definition.hasOwnProperty('defaultValue')
          ? definition.defaultValue
          : currentValue
        : currentValue

    const builtDefinition = {
      id: builtDefinitions.length,
      ...definition,
      value: valueWithFallback,
    }

    if (typeof builtDefinition.isVisible === 'function' && !builtDefinition.isVisible(selectedCase)) {
      continue
    }

    if (typeof builtDefinition?.getActionButtons === 'function') {
      builtDefinition.actionButtons = builtDefinition.getActionButtons(selectedCase)
    }

    builtDefinitions.push(builtDefinition)
  }

  return builtDefinitions
}

export function getCalculationSettings(selectedCase) {
  return buildDefinitionsForSection(selectedCase, 'CalculationSettings')
}

export function getDrillingParameters(selectedCase) {
  return buildDefinitionsForSection(selectedCase, 'DrillingParameters')
}

export function getMudParameters(selectedCase) {
  return buildDefinitionsForSection(selectedCase, 'Mud')
}

export function getTorqueAndDragSettings(selectedCase) {
  return buildDefinitionsForSection(selectedCase, 'TorqueAndDrag')
}

export function getSurgeAndSwabParameters(selectedCase) {
  return buildDefinitionsForSection(selectedCase, 'SurgeAndSwabParameters')
}

export function getCasingStandoffParameters(selectedCase) {
  return buildDefinitionsForSection(selectedCase, 'CasingStandoff')
}

export function getWellControlParameters(selectedCase) {
  return buildDefinitionsForSection(selectedCase, 'WellControl')
}

export function getHydraulicsParameters(selectedCase) {
  return buildDefinitionsForSection(selectedCase, 'Hydraulics')
}

export function getCementingParameters(selectedCase) {
  return buildDefinitionsForSection(selectedCase, 'Cementing')
}

export function normalizeDropDownOptions(parameterDefinition, selectedCase) {
  if (!parameterDefinition) return []

  // If someone passes explicit options later, support it.
  if (Array.isArray(parameterDefinition.dropDownOptions)) {
    return parameterDefinition.dropDownOptions.map((option) =>
      typeof option === 'string' ? { label: unescapeHtml(option), value: option } : option,
    )
  }

  const dropDownSourceKey = parameterDefinition.dropDownField
  if (typeof dropDownSourceKey !== 'string' || dropDownSourceKey === '') return []

  const dropDownSources = selectedCase?.engineeringDropDowns
  const sourceArray = dropDownSources?.[dropDownSourceKey]
  if (!Array.isArray(sourceArray)) return []

  return sourceArray.map((option) => ({ label: option, value: option }))
}
